import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Skeleton, useMediaQuery, useTheme } from '@mui/material';
import { useUserStore } from 'store';

import { useUnsavedChanges } from '../../hooks/useUnsavedChanges';
import DashboardDialogs from './DashboardDialogs';
import DashboardOnboarding from './DashboardOnboarding';
import EditToolbar from './EditToolbar';
import EmptyDashboard from './EmptyDashboard';
import GreetingRow from './GreetingRow';
import MobileWidgetList from './MobileWidgetList';
import PresetPickerDialog from './PresetPickerDialog';
import SystemBanners from './SystemBanners';
import UndoSnackbar from './UndoSnackbar';
import WidgetGalleryDialog from './WidgetGalleryDialog';
import WidgetGrid from './WidgetGrid';
import WidgetSettingsPopover from './WidgetSettingsPopover';
import WidgetSlot from './WidgetSlot';
import { WIDGET_BY_ID } from './registry';
import { selectCurrentLayout, selectCurrentPresetId, selectIsDirty, useDashboardStore } from './storeDashboard';
import { reset as resetData, startTicker, stopTicker } from './storeDashboardData';
import { visibleWidgets } from './visibility';
import { isSimplifiedUi } from '../../utils/access';

/**
 * Muokattava työpöytä: the landing page for every organization since 2026-09-06.
 */
export default function WidgetDashboard() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user, currentOrganization } = useUserStore();
  const orgId = currentOrganization?.organization_id;
  const simplified = isSimplifiedUi(currentOrganization);

  const store = useDashboardStore();
  const { mode, status, saving, saveError, lastRemoved, scrollToId } = store;
  const layout = useMemo(() => selectCurrentLayout(store, currentOrganization), [store, currentOrganization]);
  const widgets = useMemo(() => visibleWidgets(layout, currentOrganization), [layout, currentOrganization]);
  const dirty = selectIsDirty(store, currentOrganization);
  const presetId = selectCurrentPresetId(store, currentOrganization);
  const edit = mode === 'edit';

  const [galleryOpen, setGalleryOpen] = useState(false);
  const [presetsOpen, setPresetsOpen] = useState(false);
  const [settings, setSettings] = useState(null); // { id, anchorEl }

  useEffect(() => {
    if (!orgId) return undefined;
    store.load(orgId, simplified);
    resetData();
    return () => {
      if (useDashboardStore.getState().mode === 'edit') useDashboardStore.getState().cancelEdit();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, simplified]);

  useEffect(() => {
    startTicker();
    return stopTicker;
  }, []);

  const { UnsavedChangesDialog } = useUnsavedChanges({ hasChanges: dirty, onSave: store.save, mode: 'page' });

  const { moveById, moveBy, resizeGroup, remove, restoreOrder, setOpts } = store;
  const actions = useMemo(
    () => ({ moveById, moveBy, resizeGroup, remove, restoreOrder, setOpts: (id, opts) => setOpts(id, opts, currentOrganization) }),
    [moveById, moveBy, resizeGroup, remove, restoreOrder, setOpts, currentOrganization]
  );

  const onOpenSettings = useCallback((id, anchorEl) => setSettings({ id, anchorEl }), []);
  const enterEdit = () => store.enterEdit(currentOrganization);
  const openGallery = () => {
    if (!edit) enterEdit();
    setGalleryOpen(true);
  };
  const openPresets = () => {
    if (!edit) enterEdit();
    setPresetsOpen(true);
  };

  const settingsEntry = settings ? WIDGET_BY_ID[settings.id] : null;
  const settingsOpts = settings ? layout.widgets.find((widget) => widget.id === settings.id)?.opts : null;

  // The dotted "canvas" marks only the area where tiles can actually be dropped: the desktop
  // grid. The toolbar, banners, mobile list and empty state stay on the plain page background.
  // Negative margin + equal padding keep the tile columns exactly where they are in view mode.
  const editCanvas = {
    backgroundImage: 'radial-gradient(#d3dae3 1px, transparent 1px)',
    backgroundSize: '20px 20px',
    backgroundPosition: '10px 10px',
    bgcolor: 'rgba(148, 163, 184, 0.06)',
    borderRadius: '16px',
    mx: -2,
    px: 2,
    py: 2,
  };

  let body;
  if (status === 'loading' && !store.saved) {
    body = (
      <Box sx={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', gap: 2.5 }}>
        {[0, 1, 2, 3].map((index) => (
          <Skeleton key={index} variant="rounded" height={140} />
        ))}
      </Box>
    );
  } else if (widgets.length === 0) {
    body = <EmptyDashboard onAdd={openGallery} onPresets={openPresets} onRestore={() => { store.resetToClassic(currentOrganization); store.save(); }} />;
  } else if (isMobile) {
    body = edit ? (
      <MobileWidgetList widgets={widgets} actions={actions} onAddWidget={() => setGalleryOpen(true)} />
    ) : (
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gridAutoRows: 'auto', gap: 1.5 }} data-testid="widget-grid">
        {widgets.map((widget) => (
          <WidgetSlot key={widget.id} widget={widget} mode="view" isMobile sortable={false} actions={actions} onOpenSettings={onOpenSettings} />
        ))}
      </Box>
    );
  } else {
    body = (
      <Box sx={edit ? editCanvas : undefined}>
        <WidgetGrid
          widgets={widgets}
          mode={mode}
          actions={actions}
          onOpenSettings={onOpenSettings}
          onAddWidget={() => setGalleryOpen(true)}
          scrollToId={scrollToId}
          onScrolled={store.clearScrollTo}
        />
      </Box>
    );
  }

  return (
    <DashboardDialogs>
      <Box data-testid="dashboard-page">
        {edit ? (
          <EditToolbar
            isMobile={isMobile}
            saving={saving}
            saveError={saveError}
            onAdd={() => setGalleryOpen(true)}
            onPresets={() => setPresetsOpen(true)}
            onCancel={store.cancelEdit}
            onSave={store.save}
          />
        ) : (
          <GreetingRow user={user} organization={currentOrganization} isMobile={isMobile} onEdit={enterEdit} />
        )}
        <SystemBanners dim={edit} />
        {/* Onboarding sits above the grid, not in it: the card grows with the open step and a
            grid slot clipped it. Inert while editing, like the widget bodies. */}
        <DashboardOnboarding dim={edit} />
        {body}
      </Box>

      <WidgetGalleryDialog
        open={galleryOpen}
        onClose={() => setGalleryOpen(false)}
        organization={currentOrganization}
        layoutIds={layout.widgets.map((widget) => widget.id)}
        onAdd={(id, size) => store.add(id, size)}
      />
      <PresetPickerDialog
        open={presetsOpen}
        onClose={() => setPresetsOpen(false)}
        organization={currentOrganization}
        currentPresetId={presetId}
        currentCount={layout.widgets.length}
        onApply={(id) => {
          store.applyPreset(id, currentOrganization);
          setPresetsOpen(false);
        }}
        onRestoreClassic={(id) => {
          store.applyPreset(id, currentOrganization);
          setPresetsOpen(false);
        }}
      />
      <WidgetSettingsPopover
        anchorEl={settings?.anchorEl || null}
        entry={settingsEntry}
        opts={settingsOpts}
        organization={currentOrganization}
        onClose={() => setSettings(null)}
        onSave={(opts) => {
          store.setOpts(settings.id, opts, currentOrganization);
          setSettings(null);
        }}
      />
      <UndoSnackbar removed={edit ? lastRemoved : null} onUndo={store.undoRemove} onClose={store.clearUndo} />
      {UnsavedChangesDialog}
    </DashboardDialogs>
  );
}
